import { createSlice, createAsyncThunk, createEntityAdapter } from '@reduxjs/toolkit'
import { getPatientsList, getPatientsCount } from './patientApi'
import { generateQuery } from 'utils/filter'
import { transformPatients } from 'utils/transform'

const patientAdapter = createEntityAdapter()

const patients = state => state.patients
export const patientSelector = patientAdapter.getSelectors(patients)

export const getPatients = createAsyncThunk('patients/list', async ({ arg }, thunkAPI) => {
    try {
        const response = await getPatientsList(arg)
        return response.data
    } catch (error) {
        return thunkAPI.rejectWithValue(error)
    }
})

export const getPatientsWithSummary = createAsyncThunk(
    'patients/list-with-count',
    async (_, thunkAPI) => {
        try {
            const { patients } = thunkAPI.getState()
            const { page, pageSize, filterParams, recordsCount } = patients
            const query = generateQuery(filterParams)
            let count = 0

            if (page === 1) {
                const summaryResponse = await getPatientsCount('&' + query)
                if (summaryResponse.error) throw summaryResponse.error
                count = summaryResponse.data.recordsCount
                if (count === 0) {
                    return summaryResponse.data
                }
            }
            const response = await getPatientsList({
                page,
                pageSize,
                query: query ? '&' + query : '',
            })
            return {
                ...response.data,
                recordsCount: page === 1 ? count : recordsCount,
            }
        } catch (error) {
            return thunkAPI.rejectWithValue(error)
        }
    }
)

export const initialFilter = {
    date: 'all',
    dateFrom: '',
    dateTo: '',
    tag: ['all'],
    shared: false,
}
const initialFilterParams = [
    {
        type: 'sort',
        value: ['Sort.By=examDate&Sort.Desc=true'],
    },
]

const initialState = {
    ...patientAdapter.getInitialState(),
    selectedPatient: null,
    recordsCount: 0,
    filter: initialFilter,
    filterParams: initialFilterParams,
    page: 1,
    pageSize: 20,
    uiFilter: 'all',
    sort: 'dateDesc',
    search: '',
    isSearched: false,
    isFiltered: false,
    isLoading: false,
    isError: false,
}

const patientSlice = createSlice({
    name: 'patients',
    initialState,
    reducers: {
        setPatient: (state, { payload }) => {
            state.selectedPatient = payload
        },
        setPage: (state, { payload }) => {
            state.page = payload
        },
        setFilter: (state, { payload }) => {
            state.filter = payload
        },
        setUIFilter: (state, { payload }) => {
            state.uiFilter = payload
        },
        setSort: (state, { payload }) => {
            state.sort = payload
        },
        setSearch: (state, { payload }) => {
            state.isSearched = payload.status
            state.search = payload.keyword
        },
        reInitialize: state => {
            state.selectedPatient = null
            state.page = 1
        },
        resetFilter: state => {
            state.filter = initialFilter
            state.isFiltered = false
        },
        updateFilter: (state, { payload }) => {
            state.filter[payload.key] = payload.value
        },
        resetAll: state => {
            state.filter = initialFilter
            state.filterParams = initialFilterParams
            state.sort = 'dateDesc'
            state.isFiltered = false
        },
        clearSuggestion: state => {
            state.isSearched = false
            state.search = ''
        },
        setFilterParams: (state, { payload }) => {
            state.selectedPatient = null
            if (state.filterParams.findIndex(p => p.type === payload.type) < 0) {
                const data = [...state.filterParams, payload]
                state.filterParams = data
                state.isFiltered = Boolean(data.find(d => d.type === 'filter'))
            } else {
                if (payload.value && payload.value.length) {
                    const data = [
                        ...state.filterParams.filter(p => p.type !== payload.type),
                        payload,
                    ]
                    state.filterParams = data
                    state.isFiltered = Boolean(data.find(d => d.type === 'filter'))
                } else {
                    const data = state.filterParams.filter(p => p.type !== payload.type)
                    state.filterParams = data
                    state.isFiltered = Boolean(data.find(d => d.type === 'filter'))
                }
            }
        },
    },
    extraReducers: builder => {
        builder.addCase(getPatients.pending, state => {
            state.isLoading = true
            state.isError = false
        })
        builder.addCase(getPatients.fulfilled, (state, { payload }) => {
            patientAdapter.upsertMany(state, transformPatients(payload.data))
            state.isLoading = false
        })
        builder.addCase(getPatients.rejected, state => {
            state.isLoading = false
            state.isError = true
        })
        builder.addCase(getPatientsWithSummary.pending, (state, { meta }) => {
            if (state.page === 1) {
                patientAdapter.removeAll(state)
                state.recordsCount = 0
            }
            state.isLoading = true
            state.isError = false
        })
        builder.addCase(getPatientsWithSummary.fulfilled, (state, { payload }) => {
            patientAdapter.upsertMany(state, transformPatients(payload.data) || [])
            state.recordsCount = payload.recordsCount
            state.isLoading = false
        })
        builder.addCase(getPatientsWithSummary.rejected, state => {
            state.isLoading = false
            state.isError = true
        })
    },
})

export const {
    setPatient,
    setFilter,
    setUIFilter,
    setSort,
    setSearch,
    reInitialize,
    updateFilter,
    resetFilter,
    resetAll,
    clearSuggestion,
    setFilterParams,
    setPage,
} = patientSlice.actions

export default patientSlice.reducer
